import { CommonModule } from '@angular/common';
import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnChanges,
  SimpleChanges, ViewChild, ElementRef, ChangeDetectorRef, Inject
} from '@angular/core';
import isEqual from 'lodash-es/isEqual';
import { differenceInDays } from 'date-fns';

import { isNullOrUndefined } from '@app/util/util';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { CalendarDay, CalendarMonth } from '@design-system/components/molecules/calendar/entities/calendar.types';
import { DateCalendar } from '@design-system/components/organisms/date-calendar/helpers/date-calendar.helper';
import {
  DateCalendarDefaultField, DateCalendarDefaultOnlyOneDay, DateCalendarPaginatedCalendarHeight, DateCalendarSelectedEvent,
  DateCalendarPaginatedCalendarPadding, DateCalendarTwoPagesCalendarScrollWidth, DateCalendarTwoPagesCalendarWidth,
  DateCalendarField, DateCalendarType, DateCalendarDefaultDates, DateCalendarAllowSameDateSelection, DateCalendarLocale,
  DateCalendarPaginatedCalendarHeightWithInputs, DateCalendarInputStringValues, DateCalendarSelected,
  DateCalendarInputBooleanValues, DateCalendarOnePageCalendarWidth, DateCalendarOnePageCalendarScrollWidth,
  DateCalendarOnePageMaxMonthsToScroll, DateCalendarTwoPagesMaxMonthsToScroll
} from '@design-system/components/organisms/date-calendar/entities/date-calendar.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { ButtonIconSize } from '@design-system/components/molecules/button-icon/entities/button-icon.types';
import { CalendarDsComponent } from '@design-system/components/molecules/calendar/calendar.component';
import { BackgroundSurfaceDsComponent } from '@design-system/components/atoms/background-surface/background-surface.component';
import { InputTextDsComponent } from '@design-system/components/molecules/input-text/input-text.component';
import { ButtonIconDsComponent } from '@design-system/components/molecules/button-icon/button-icon.component';

@Component({
  selector: 'app-ds-date-calendar',
  templateUrl: './date-calendar.component.html',
  styleUrls: ['./date-calendar.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    CalendarDsComponent,
    BackgroundSurfaceDsComponent,
    InputTextDsComponent,
    ButtonIconDsComponent
  ]
})
export class DateCalendarDsComponent implements OnChanges {

  @Input() type = DateCalendarType.TwoPages;
  @Input() defaultField: DateCalendarField = DateCalendarDefaultField;
  @Input() resetField: DateCalendarField = DateCalendarDefaultField;
  @Input() isOnlyOneDate = DateCalendarDefaultOnlyOneDay;
  @Input() allowSameDateSelection = DateCalendarAllowSameDateSelection;
  @Input() scrollToDefaultDate = false;
  @Input() timeToScroll = 0;
  @Input() translate = 0;
  @Input() defaultDates: DateCalendarDefaultDates = [null, null];
  @Input() locale: DateCalendarLocale = null;
  @Input() minDate: Date = null;
  @Input() maxDate: Date = null;
  @Input() disabledDates: Date[] = [];
  @Input() inputsPlaceholder: DateCalendarInputStringValues;
  @Input() inputsLabel: DateCalendarInputStringValues;
  @Input() inputsVisible: DateCalendarInputBooleanValues = { left: true, right: true };
  @Input() resetCalendar = false;

  @Output() selectEvent: EventEmitter<DateCalendarSelectedEvent> = new EventEmitter<DateCalendarSelectedEvent>();
  @Output() emitPosition: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('monthsViewRef') monthsRef: ElementRef;

  public readonly prevIcon = Icon.ArrowLeft;
  public readonly nextIcon = Icon.ArrowRight;
  public readonly calendarIcon = Icon.Calendar;

  public readonly buttonIconSize = ButtonIconSize.XL;

  protected calendar: DateCalendar;

  private datesAreBeenLoaded = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    if (this.isBrowser) {
      this.calendar = new DateCalendar();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!isNullOrUndefined(this.calendar)) {
      if (changes?.resetCalendar?.currentValue) {
        this.datesAreBeenLoaded = false;
        this.calendar.selectDefaultDates([null, null], this.emit.bind(this));
      }
      if (
        changes?.locale &&
        !isEqual(changes?.locale?.currentValue, this.calendar?.locale)
      ) {
        this.calendar.setLocale(changes?.locale?.currentValue);
      }
      if (
        changes?.defaultField &&
        !isEqual(changes?.defaultField?.currentValue, this.calendar?.field)
      ) {
        this.calendar.setField(changes?.defaultField?.currentValue);
      }
      if (
        changes?.minDate &&
        !isEqual(changes?.minDate?.currentValue, this.calendar?.minDate)
      ) {
        this.calendar.setMinDate(changes?.minDate?.currentValue);
      }
      if (
        changes?.maxDate &&
        !isEqual(changes?.maxDate?.currentValue, this.calendar?.maxDate)
      ) {
        this.calendar.setMaxDate(changes?.maxDate?.currentValue);
      }
      if (
        changes?.disabledDates &&
        !isEqual(changes?.disabledDates?.currentValue, this.calendar?.disabledDates)
      ) {
        this.calendar.setDisabledDates(changes?.disabledDates?.currentValue);
      }
      if (
        (changes?.allowSameDateSelection &&
          !isEqual(changes?.allowSameDateSelection?.currentValue, this.calendar?.allowSameDateSelection)
        ) || (changes?.isOnlyOneDate &&
          !isEqual(changes?.isOnlyOneDate?.currentValue, this.calendar?.isOnlyOneDate)
        )
      ) {
        this.datesAreBeenLoaded = false;
        if (changes?.allowSameDateSelection) {
          this.calendar.setAllowSameDateSelection(changes?.allowSameDateSelection?.currentValue);
        }
        if (changes?.isOnlyOneDate) {
          this.calendar.setOnlyOneDate(changes?.isOnlyOneDate?.currentValue);
          if (isNullOrUndefined(changes?.defaultField)) {
            this.calendar.setField(this.resetField);
          }
        }
        if (!this.defaultDates?.every(date => isNullOrUndefined(date)) && !this.datesAreBeenLoaded) {
          this.datesAreBeenLoaded = true;
          this.calendar.selectDefaultDates(this.defaultDates, this.emit.bind(this));
        }
      }
      if (
        !this.datesAreBeenLoaded &&
        (changes?.defaultDates && !isEqual(changes?.defaultDates?.currentValue, this.calendar?.defaultDates))
      ) {
        this.datesAreBeenLoaded = true;
        this.calendar.selectDefaultDates(changes?.defaultDates?.currentValue, this.emit.bind(this));
      }
      if (changes?.scrollToDefaultDate?.currentValue) {
        setTimeout(() => this.scrollToAnchor(), this.timeToScroll);
      }
    }
  }

  get months(): CalendarMonth[] {
    return this.calendar?.months;
  }

  get calendarField(): DateCalendarField {
    return this.calendar?.field;
  }

  get selectedDates(): DateCalendarSelected {
    return this.calendar.selectedDates;
  }

  get leftIsFilled(): boolean {
    return !!this.selectedDates?.left?.formatted;
  }

  get rightIsFilled(): boolean {
    return !!this.selectedDates?.right?.formatted;
  }

  get canMoveCalendar(): boolean {
    return this.paginatedCalendar;
  }

  get canMoveToLeft(): boolean {
    return this.translate < 0;
  }

  get canMoveToRight(): boolean {
    return this.translate > (-this.calendarScrollWidth * this.calendarMaxPages);
  }

  get hasInputs(): boolean {
    return this.inputsVisible.left || this.inputsVisible.right;
  }

  get width(): string {
    return this.type === DateCalendarType.TwoPages ? DateCalendarTwoPagesCalendarWidth :
      this.type === DateCalendarType.OnePage ? DateCalendarOnePageCalendarWidth : null;
  }

  get height(): string {
    return this.paginatedCalendar ? this.hasInputs ? DateCalendarPaginatedCalendarHeightWithInputs :
      DateCalendarPaginatedCalendarHeight : null;
  }

  get padding(): string {
    return this.paginatedCalendar ? DateCalendarPaginatedCalendarPadding : null;
  }

  private get calendarScrollWidth(): number {
    return this.type === DateCalendarType.TwoPages ? DateCalendarTwoPagesCalendarScrollWidth :
      this.type === DateCalendarType.OnePage ? DateCalendarOnePageCalendarScrollWidth : 0;
  }

  private get calendarMaxPages(): number {
    return this.type === DateCalendarType.TwoPages ? DateCalendarTwoPagesMaxMonthsToScroll :
      this.type === DateCalendarType.OnePage ? DateCalendarOnePageMaxMonthsToScroll : 0;
  }

  private get paginatedCalendar(): boolean {
    return this.type === DateCalendarType.OnePage || this.type === DateCalendarType.TwoPages;
  }

  private get daysBetweenTwoDates(): number {
    if (this.calendar.isOnlyOneDate) {
      return 0;
    } else if (this.calendar.selectedDates.right?.date && this.calendar.selectedDates.left?.date) {
      return differenceInDays(
        this.calendar.selectedDates.right.date, this.calendar.selectedDates.left.date
      );
    } else {
      return null;
    }
  }

  public fieldIsActivated(field: DateCalendarField): boolean {
    return field === this.calendar.field;
  }

  public fieldIsDisabled(field: DateCalendarField): boolean {
    return field !== this.calendar.field && this.isOnlyOneDate;
  }

  public setField(field: DateCalendarField): void {
    this.calendar.setField(field);
  }

  public selectDate(day: CalendarDay): void {
    this.calendar.selectDay(day, this.emit.bind(this));
    this.emitPosition.emit(this.translate);
  }

  public moveControl(direction: boolean): void {
    const move = this.translate + (direction ? this.calendarScrollWidth : -this.calendarScrollWidth);
    this.translate = move > 0 ? 0 : move >= (-this.calendarScrollWidth * this.calendarMaxPages) ?
      move : (-this.calendarScrollWidth * this.calendarMaxPages);
  }

  private scrollToAnchor(): void {
    if (!isNullOrUndefined(this.calendar?.anchor) && this.monthsRef?.nativeElement) {
      const id = this.calendar.anchor;
      const month = this.monthsRef.nativeElement.querySelector(`.${id}`);
      if (this.paginatedCalendar) {
        if (!isNullOrUndefined(month)) {
          this.translate =
            parseInt((
              parseInt(month?.offsetLeft, 10) / -this.calendarScrollWidth
            ).toFixed(), 10) * this.calendarScrollWidth;
          this.emitPosition.emit(this.translate);
          this.cdr.detectChanges();
        }
      } else {
        month?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  private emit(): void {
    this.selectEvent.emit({
      ...this.calendar.selectedDates,
      field: this.calendar.field,
      days: !isNullOrUndefined(this.daysBetweenTwoDates) ? (this.daysBetweenTwoDates + 1) : null,
      nights: this.daysBetweenTwoDates,
    });
  }

}
